/**
 * How full is a technician's day — and in what unit (#630, following #587).
 *
 * #587 made **points the primary weight read** on the Day board: how full a
 * tech's day is comes off the points meter, with hours as a thin secondary
 * line. The Week view never got that treatment, so the office read the Day
 * board in points and the Week board in hours — the same question answered in
 * two units, on two screens, a click apart.
 *
 * The rule lives here rather than in either board so they cannot drift, and so
 * the fallback (a tech with no points target still reads in hours) is stated
 * once instead of guessed twice.
 */

export type LoadBasis = "points" | "hours";

export interface LoadInputs {
    bookedPoints?: number | null;
    pointsTarget?: number | null;
    pointsCap?: number | null;
    bookedMinutes: number;
    capacityMinutes: number;
}

export interface LoadRead {
    /** Fullness, where 1 = exactly at target. Drives the colour band. */
    ratio: number;
    /** Which unit the ratio was computed in — what the cell should show first. */
    basis: LoadBasis;
    /** Booked amount in the chosen unit, already rounded for display. */
    booked: number;
    /** What they're measured against, in the chosen unit. */
    target: number;
    /** Past the hard cap (points) — the band that must read as red. */
    overCap: boolean;
}

/**
 * Points win when the tech actually has a target to measure against.
 *
 * A points target of 0 or null means nobody set one, and dividing by it would
 * either throw or report every tech as infinitely overbooked — so those fall
 * back to hours, which is what the Day board does and what Week did before.
 * Booked points of 0 is NOT a fallback trigger: a tech with a target and an
 * empty day is legitimately at zero, and showing that as "no data" would hide
 * the emptiest day of the week.
 */
export function readLoad(input: LoadInputs): LoadRead {
    const { bookedPoints, pointsTarget, pointsCap, bookedMinutes, capacityMinutes } = input;
    const hasPointsTarget = pointsTarget != null && pointsTarget > 0;

    if (hasPointsTarget) {
        const target = pointsTarget;
        const booked = bookedPoints ?? 0;
        // A hard cap below the soft target would make "over cap" fire before
        // "at target", which reads as nonsense on the meter. Treat the target
        // as the floor for the cap.
        const hardCap = pointsCap != null && pointsCap > 0 ? Math.max(pointsCap, target) : target;
        return {
            ratio: booked / target,
            basis: "points",
            booked: Math.round(booked * 10) / 10,
            target,
            overCap: booked > hardCap,
        };
    }

    // Hours fallback. Guard the divisor the same way the Day board does — a
    // capacity under an hour is a data problem, not a reason to divide by zero.
    const safeCapacity = Math.max(60, capacityMinutes);
    const hours = bookedMinutes / 60;
    const capHours = safeCapacity / 60;
    return {
        ratio: bookedMinutes / safeCapacity,
        basis: "hours",
        booked: Math.round(hours * 10) / 10,
        target: Math.round(capHours * 10) / 10,
        overCap: bookedMinutes > safeCapacity,
    };
}

/** The primary line on a cell: "14 / 20 pts" or "6.5 / 8h". */
export function formatLoad(read: LoadRead): string {
    return read.basis === "points" ? `${read.booked} / ${read.target} pts` : `${read.booked} / ${read.target}h`;
}
